"use client";

import React, { useState, useEffect } from "react";
import { HeaderSection } from "@/components/shared/header-section";
import { PageContainer } from "@/components/shared/page-container";
import { useLazyQuery } from "@apollo/client";
import { DataTableManual } from "@/components/shared/data-table-manual/data-table-manual";
import { columns } from "./components/columns";
import { DataTableToolbar } from "@/components/shared/data-table-manual/data-table-toolbar-manual";
import { DELETE_MANY_PRICELISTS, GET_PRICELISTS } from "./priceListQueries";
import { useDeleteConfirmation } from "@/hooks/useDeleteConfirmation";
import { PageContent } from "@/components/shared/page-content";
import { getOrderSortParams } from "@/utils/orderUtils";

export default function PriceLists() {
  const [searchTerm, setSearchTerm] = useState("");
  const [tableData, setTableData] = useState([]);
  const [tableDataCount, setTableDataCount] = useState(0);
  const [order, setOrder] = useState([]);
  const [filters, setFilters] = React.useState<Map<string, Set<string>>>(
    new Map()
  );

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const [loadPriceLists, { data, error, loading }] = useLazyQuery(
    GET_PRICELISTS,
    {
      variables: {},
      fetchPolicy: "cache-and-network",
    }
  );

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_PRICELISTS,
    refetchQueries: ["GET_PRICELISTS"],
    messages: {
      success: "Price Lists deleted successfully",
      error: "Failed to delete price lists",
    },
    itemName: "price list",
  });

  // Load price lists data
  useEffect(() => {
    loadPriceLists({
      variables: {
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        orderBy: getOrderSortParams(order),
      },
    });
  }, [searchTerm, pagination, order]);

  // Reset to first page when search or page size changes
  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 });
  }, [searchTerm, pagination.pageSize, order]);

  // Update table data when query results change
  useEffect(() => {
    if (data) {
      setTableData(data.findManyPriceList);
      setTableDataCount(data.findManyPriceListCount ?? 0);
    }
  }, [data]);

  return (
    <PageContent>
      <HeaderSection title="Price Lists" />

      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        isLoading={loading}
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: "Filter price lists...",
                accessor: "name",
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [],
            }}
          />
        )}
      />
    </PageContent>
  );
}
